import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../../../auth/api";
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiPhotograph,
  HiVideoCamera,
  HiSearch,
  HiCheckCircle,
  HiXCircle,
  HiEye,
} from "react-icons/hi";

import DeleteConfirmationModal from "../../../components/Home/DeleteConfirmationModal";

function GalleryList() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [formatFilter, setFormatFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth("GET", "adminGallery");
      setGalleryItems(response.data || []);
    } catch (error) {
      toast.error("Failed to load gallery items");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await fetchWithAuth("PATCH", `gallery/${id}/status`);
      toast.success(response.data.message || "Status updated successfully");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await fetchWithAuth("DELETE", `deleteGallery/${deleteId}`);
      toast.success("Gallery item deleted successfully");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to delete gallery item");
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  const filteredItems = galleryItems.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = typeFilter === "all" || item.media_type === typeFilter;
    const matchesFormat = formatFilter === "all" || item.format_type === formatFilter;

    return matchesSearch && matchesType && matchesFormat;
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Gallery Management
          </h1>
          <p className="text-xs sm:text-sm font-medium text-slate-500 mt-1">
            Manage photos, widescreen videos, and shorts/reels displayed on your website gallery.
          </p>
        </div>
        <Link
          to="/admin/dashboard/createGallery"
          className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs sm:text-sm transition-all shadow-lg shadow-blue-500/20 active:scale-95 self-start sm:self-auto"
        >
          <HiPlus size={18} />
          Add Gallery Media
        </Link>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:w-80">
          <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search by title or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:border-blue-500 font-medium"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Media Types</option>
            <option value="image">Photos Only</option>
            <option value="video">Videos Only</option>
          </select>

          <select
            value={formatFilter}
            onChange={(e) => setFormatFilter(e.target.value)}
            className="px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Formats</option>
            <option value="16:9">16:9 (Landscape / Widescreen)</option>
            <option value="9:16">9:16 (Portrait / Shorts / Reels)</option>
          </select>
        </div>
      </div>

      {/* Grid Display */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-4">
            Loading Gallery Media...
          </p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <HiPhotograph className="mx-auto text-slate-300 mb-3" size={48} />
          <h3 className="text-base font-bold text-slate-700">No Gallery Media Found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Click "Add Gallery Media" above to upload images to Cloudinary or add video links.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col group hover:shadow-md transition-all duration-300"
            >
              {/* Media Preview Container */}
              <div
                className={`relative bg-slate-900 overflow-hidden flex items-center justify-center ${
                  item.format_type === "9:16" ? "h-72" : "h-48"
                }`}
              >
                {item.media_type === "image" ? (
                  <img
                    src={item.url}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="relative w-full h-full bg-slate-950 flex items-center justify-center">
                    {item.thumbnail_url ? (
                      <img
                        src={item.thumbnail_url}
                        alt={item.title}
                        className="w-full h-full object-cover opacity-80"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-slate-400">
                        <HiVideoCamera size={40} />
                        <span className="text-[10px] font-black tracking-widest uppercase">Video Link</span>
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg">
                        <HiVideoCamera size={24} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Badges */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span
                    className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${
                      item.media_type === "image"
                        ? "bg-blue-600 text-white"
                        : "bg-purple-600 text-white"
                    }`}
                  >
                    {item.media_type === "image" ? "Photo" : "Video"}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-slate-900/80 text-slate-200 border border-white/20 backdrop-blur-sm">
                    {item.format_type}
                  </span>
                </div>

                <button
                  onClick={() => handleToggleStatus(item.id)}
                  className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider flex items-center gap-1 transition-all ${
                    item.is_active
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-700 text-slate-300"
                  }`}
                  title="Click to toggle status"
                >
                  {item.is_active ? (
                    <>
                      <HiCheckCircle size={12} /> Active
                    </>
                  ) : (
                    <>
                      <HiXCircle size={12} /> Hidden
                    </>
                  )}
                </button>
              </div>

              {/* Item Info */}
              <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <span className="text-[9px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-100">
                    {item.category || "General"}
                  </span>
                  <h3 className="text-base font-bold text-slate-900 mt-2 line-clamp-1">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-2 truncate font-mono bg-slate-50 p-1.5 rounded border border-slate-100">
                    {item.url}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                    title="View Media"
                  >
                    <HiEye size={18} />
                  </a>

                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/dashboard/editGallery/${item.id}`}
                      className="p-2 text-slate-500 hover:text-blue-600 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Edit Item"
                    >
                      <HiPencil size={18} />
                    </Link>
                    <button
                      onClick={() => confirmDelete(item.id)}
                      className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      title="Delete Item"
                    >
                      <HiTrash size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

export default GalleryList;
